import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Download, Stethoscope } from 'lucide-react'
import { getClientDetailReport } from '../lib/api'

const COLUMNS = [
  { key: 'shipment_id',           label: 'Shipment #' },
  { key: 'order_number',          label: 'Order #' },
  { key: 'tracking_number',       label: 'Tracking #' },
  { key: 'service',               label: 'Service' },
  { key: 'recipient',             label: 'Recipient' },
  { key: 'ship_date',             label: 'Ship Date' },
  { key: 'packing_slip_printed',  label: 'Packing Slip Printed' },
  { key: 'label_created',         label: 'Label Created' },
  { key: 'label_printed',         label: 'Label Printed' },
  { key: 'marketplace_notified',  label: 'Marketplace Notified' },
  { key: 'shipment_notification', label: 'Shipment Notification' },
  { key: 'delivery_notification', label: 'Delivery Notification' },
  { key: 'carrier_fee',           label: 'Carrier Fee', align: 'right' },
  { key: 'city',                  label: 'City' },
  { key: 'postal_code',           label: 'Postal Code' },
  { key: 'item_name',             label: 'Item Name' },
  { key: 'item_quantity',         label: 'Item Qty', align: 'right' },
  { key: 'item_sku',              label: 'Item SKU' },
  { key: 'lot_no',                label: 'Lot No.' },
]

function formatCell(key, value) {
  if (value === null || value === undefined || value === '') return '—'
  if (key === 'ship_date' || key === 'label_created') {
    try { return new Date(value).toISOString().slice(0, 16).replace('T', ' ') } catch { return String(value) }
  }
  if (key === 'carrier_fee') {
    return `$${Number(value).toFixed(2)}`
  }
  return String(value)
}

function toCsv(rows) {
  const header = COLUMNS.map(c => c.label).join(',')
  const escape = (v) => {
    if (v === null || v === undefined) return ''
    const s = String(v).replace(/"/g, '""')
    return /[",\n]/.test(s) ? `"${s}"` : s
  }
  const lines = rows.map(r => COLUMNS.map(c => escape(r[c.key])).join(','))
  return [header, ...lines].join('\n')
}

export default function DetailReportTab({ clientId }) {
  const now = new Date()
  const [year, setYear]   = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['detail-report', clientId, year, month],
    queryFn: () => getClientDetailReport(clientId, year, month),
    enabled: !!clientId,
  })

  const rows = data?.rows || []

  function downloadCsv() {
    const csv = toCsv(rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `detail-report-${year}-${String(month).padStart(2, '0')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Stethoscope size={16} className="text-emerald-600" />
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Detail Report</h3>
              <p className="text-xs text-slate-400">
                Per-shipment breakdown from ShipStation — incl. orders marked as shipped. One row per item.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              {months.map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              onClick={downloadCsv}
              disabled={!rows.length}
              className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.response?.data?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                {COLUMNS.map(c => (
                  <th
                    key={c.key}
                    className={`px-3 py-3 text-${c.align || 'left'} font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {(isLoading || isFetching) ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="py-12 text-center">
                    <Loader2 size={16} className="inline animate-spin text-slate-400" />
                    <p className="text-slate-400 text-xs mt-2">Pulling shipments live from ShipStation…</p>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="py-12 text-center text-slate-400 text-xs">
                    No shipments for this period.
                  </td>
                </tr>
              ) : rows.map((r, i) => (
                <tr key={i} className={i % 2 ? 'bg-slate-50/30' : ''}>
                  {COLUMNS.map(c => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 text-${c.align || 'left'} text-slate-700 whitespace-nowrap`}
                    >
                      {formatCell(c.key, r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={COLUMNS.length} className="px-3 py-2.5 text-xs text-slate-500">
                    {data?.shipment_count?.toLocaleString()} shipment{data?.shipment_count !== 1 ? 's' : ''}
                    {data?.marked_shipped_count > 0 && (
                      <> · {data.marked_shipped_count.toLocaleString()} marked-as-shipped</>
                    )}{' '}·{' '}
                    {rows.length.toLocaleString()} item row{rows.length !== 1 ? 's' : ''}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 px-1">
        Notification timestamps (Packing Slip Printed, Label Printed, Marketplace Notified, Delivery Notification)
        are only available in ShipStation's UI CSV export — they aren't exposed by the public API and will appear
        as "—". Lot numbers are read from each item's options (any option whose name contains "lot" or "batch").
      </p>
    </div>
  )
}
